// components/chat/IncomingCallToast.jsx
// Incoming-call surface — spec cards 01 (full) + 02 (mini banner) + quick replies.
// Provider wiring is unchanged (rendered by CallProvider); this only owns the visuals.
//   onAccept()        — accept as-is (video if a video call)
//   onAcceptAudio()   — accept camera-off (audio only)
//   onDecline()       — plain decline
//   onQuickReply(text)— decline with a canned reply (respond channel)
//   preferMini        — start collapsed as the corner banner (expands on hover/click)
import React, { useState, useEffect } from "react";
import { Video, Mic, Phone, PhoneOff, Calendar } from "lucide-react";

const GREEN = "#00FF99";
const RED   = "#ff5f56";

const ini = (n) => {
  const p = (n || "?").trim().split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] || "?") + (p.length > 1 ? p[p.length - 1][0] : "")).toUpperCase();
};

const QUICK_REPLIES = [
  { lead: "Can't talk —",          strong: "call you in 15?",             text: "Can't talk right now — call you in 15?" },
  { lead: "In the middle of something —", strong: "message me",           text: "In the middle of something — message me instead?" },
  { lead: "Wrong time —",          strong: "pick a slot on my calendar",  text: "Wrong time — pick a slot on my calendar?" },
];

const RING_CSS = `@keyframes kwCallRing{0%{transform:scale(.9);opacity:0}25%{opacity:1}100%{transform:scale(1.08);opacity:0}}`;

function Avatar({ name, size = 52, rings = true, accent = GREEN }) {
  const ringBase = {
    position: "absolute", borderRadius: "999px", border: `1.5px solid ${accent}66`,
    animation: "kwCallRing 1.8s ease-out infinite", pointerEvents: "none",
  };
  return (
    <div style={{ position: "relative", width: size, height: size, flex: "none" }}>
      <div style={{
        width: size, height: size, borderRadius: "999px",
        background: "radial-gradient(circle at 35% 30%,#1c2b24,#0b120e 70%)",
        border: `1px solid ${accent}4d`, display: "grid", placeItems: "center",
        fontFamily: "'Space Grotesk',sans-serif", fontSize: size * 0.33, fontWeight: 600, color: accent,
      }}>{ini(name)}</div>
      {rings && <>
        <span style={{ ...ringBase, inset: -7 }} />
        <span style={{ ...ringBase, inset: -14, borderColor: `${accent}2e`, animationDelay: ".45s" }} />
      </>}
    </div>
  );
}

export default function IncomingCallToast({ call, onAccept, onAcceptAudio, onDecline, onQuickReply, preferMini = false }) {
  const [showQR,    setShowQR]    = useState(false);
  const [expanded,  setExpanded]  = useState(!preferMini);

  // Reset transient UI whenever a new call arrives (or clears).
  useEffect(() => { setShowQR(false); setExpanded(!preferMini); }, [call?.room_id, preferMini]);

  if (!call) return null;
  const isAudio = call.audio_only;
  const TypeIcon = isAudio ? Mic : Video;
  const typeLabel = `Incoming ${isAudio ? "audio" : "video"} call`;

  const declineTap = () => { if (!showQR) setShowQR(true); else onDecline?.(); };
  const pickReply  = (text) => onQuickReply?.(text);

  // ── Mini banner (spec 02) — corner pill; expands to full on hover/click ──
  if (!expanded) {
    return (
      <>
        <style>{RING_CSS}</style>
        <div
          onMouseEnter={() => setExpanded(true)}
          onClick={() => setExpanded(true)}
          style={{
            position: "fixed", top: 20, right: 20, zIndex: 10085, width: 300, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 10, padding: 8,
            background: "rgba(13,13,13,0.97)", border: `1px solid ${GREEN}4d`, borderRadius: 999,
            boxShadow: "0 12px 40px rgba(0,0,0,.55),0 0 20px rgba(0,255,153,.08)", backdropFilter: "blur(12px)",
          }}>
          <Avatar name={call.caller_name} size={38} rings />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{call.caller_name}</div>
            <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.55)", marginTop: 1 }}>{isAudio ? "Audio call…" : "Video call…"}</div>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
            <button title="Decline" onClick={() => setExpanded(true)}
              style={{ width: 36, height: 36, borderRadius: 999, border: `1px solid ${RED}66`, background: "rgba(255,95,86,0.14)", color: RED, display: "grid", placeItems: "center", cursor: "pointer" }}>
              <PhoneOff size={15} strokeWidth={1.9} />
            </button>
            <button title="Accept" onClick={() => onAccept?.(call.room_id, call.audio_only)}
              style={{ width: 36, height: 36, borderRadius: 999, border: "none", background: GREEN, color: "#000", display: "grid", placeItems: "center", cursor: "pointer" }}>
              <Phone size={15} strokeWidth={1.9} />
            </button>
          </div>
        </div>
      </>
    );
  }

  // ── Full card (spec 01) ──
  return (
    <>
      <style>{RING_CSS}</style>
      <div style={{
        position: "fixed", top: 24, left: "50%", transform: "translateX(-50%)", zIndex: 10085,
        width: 320, padding: 14, background: "rgba(13,13,13,0.97)", border: `1px solid ${GREEN}4d`,
        borderRadius: 20, boxShadow: "0 16px 50px rgba(0,0,0,.6),0 0 24px rgba(0,255,153,.08)", backdropFilter: "blur(12px)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar name={call.caller_name} size={52} rings />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>{call.caller_name}</div>
            <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)", marginTop: 2, display: "flex", gap: 6, alignItems: "center" }}>
              <TypeIcon size={11} strokeWidth={1.9} color={GREEN} /> {typeLabel}
            </div>
          </div>
        </div>

        {/* Context line — only when the invite carries a meeting title */}
        {call.meeting_title && (
          <div style={{
            marginTop: 11, display: "flex", gap: 7, alignItems: "center",
            background: "rgba(0,255,153,0.12)", border: `1px solid ${GREEN}4d`, borderRadius: 11,
            padding: "7px 10px", fontSize: 10.5, color: "rgba(255,255,255,0.85)",
          }}>
            <Calendar size={12} strokeWidth={1.9} color={GREEN} style={{ flex: "none" }} />
            Scheduled: {call.meeting_title}
          </div>
        )}

        {/* Actions: Decline · audio-only accept · Accept */}
        <div style={{ display: "flex", gap: 8, marginTop: 13 }}>
          <button onClick={declineTap}
            style={{ flex: 1, display: "flex", gap: 7, alignItems: "center", justifyContent: "center", borderRadius: 999, padding: 10, fontSize: 12, fontWeight: 600, cursor: "pointer", background: "rgba(255,95,86,0.14)", border: `1px solid ${RED}66`, color: RED }}>
            <PhoneOff size={15} strokeWidth={1.9} /> Decline
          </button>
          <button title="Accept audio only (camera off)" onClick={() => onAcceptAudio?.(call.room_id)}
            style={{ width: 42, display: "grid", placeItems: "center", borderRadius: 999, padding: "10px 0", cursor: "pointer", background: "transparent", border: "1px solid rgba(255,255,255,0.10)", color: "rgba(255,255,255,0.55)" }}>
            <Mic size={15} strokeWidth={1.9} />
          </button>
          <button onClick={() => onAccept?.(call.room_id, call.audio_only)}
            style={{ flex: 1, display: "flex", gap: 7, alignItems: "center", justifyContent: "center", borderRadius: 999, padding: 10, fontSize: 12, fontWeight: 600, cursor: "pointer", background: GREEN, border: "none", color: "#000" }}>
            {isAudio ? <Phone size={15} strokeWidth={1.9} color="#000" /> : <Video size={15} strokeWidth={1.9} color="#000" />} Accept
          </button>
        </div>

        {/* Quick replies — revealed by the first Decline tap */}
        {showQR && (
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
            {QUICK_REPLIES.map((q) => (
              <button key={q.text} onClick={() => pickReply(q.text)}
                style={{ textAlign: "left", background: "#131313", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 11, padding: "8px 11px", fontSize: 11.5, color: "rgba(255,255,255,0.85)", cursor: "pointer" }}>
                {q.lead} <b style={{ color: GREEN, fontWeight: 600 }}>{q.strong}</b>
              </button>
            ))}
            <button onClick={() => onDecline?.()}
              style={{ textAlign: "center", background: "transparent", border: "none", padding: "4px 0 0", fontSize: 10.5, color: "rgba(255,255,255,0.35)", cursor: "pointer" }}>
              Just decline
            </button>
          </div>
        )}
      </div>
    </>
  );
}
